// Dashboard — reworked to match buildx-dashboard-mockup.html top-to-bottom:
// Health strip → Cloudflare cost (billing summary + budget bar) → Quota usage →
// [Errors today] → [Trends] → Billing history (chart + table) → [Pytha].
// The three bracketed sections are owned by another agent and wired in by the
// integrator at the MOUNT markers below. Everything binds to live /api/* via the
// `api` client; each async section has loading/empty/error states. Where the API
// lacks a mockup field (see docs/04_UI_GAP_LIST.md) we render with available data
// and a small muted note — never fabricated numbers.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  isUnauthorized,
  type StatusResponse,
  type ServiceStatus,
  type BillingCurrent,
  type BillingResource,
  type BillingMonthRow,
  type AlertRow,
  type CostCategory,
} from "../lib/api";
import {
  Card,
  SectionTitle,
  StatusPill,
  StatusPillDot,
  Banner,
  ServiceBorderCard,
  ProgressBar,
  normalizeHealth,
} from "../components/ui";
import { Modal } from "../components/Modal";
import ErrorsToday from "../components/ErrorsToday";
import TrendsSection from "../components/TrendsSection";
import PythaTable from "../components/PythaTable";
import { Header } from "../components/Header";
import { BudgetGauge } from "../components/BudgetGauge";
import { BillingHistoryChart } from "../components/BillingHistoryChart";
import { usd, pct, ms, monthLabel, big, shortTime } from "../lib/format";

interface DashData {
  status: StatusResponse;
  billing: BillingCurrent;
  history: BillingMonthRow[];
  alerts: AlertRow[];
}

type LoadState =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "ready"; data: DashData };

export default function Dashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ phase: "loading" });
  const [refreshing, setRefreshing] = useState(false);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  // Bumped on every auto/manual refresh; self-fetching sections refetch on change.
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const [status, billing, history, alerts] = await Promise.all([
        api.status(),
        api.billingCurrent(),
        api.billingHistory(12),
        api.alerts(true),
      ]);
      setState({ phase: "ready", data: { status, billing, history, alerts } });
      setSyncedAt(new Date().toISOString());
    } catch (e) {
      if (isUnauthorized(e)) {
        navigate("/login", { replace: true, state: { from: "/" } });
        return;
      }
      setState({
        phase: "error",
        message: e instanceof Error ? e.message : "Failed to load dashboard",
      });
    }
  }, [navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-refresh every 60s; skipped while the tab is hidden (no wasted polls).
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.hidden) return;
      void load();
      setRefreshTick((t) => t + 1);
    }, 60_000);
    return () => window.clearInterval(id);
  }, [load]);

  // Dynamic tab title: plain when healthy, issue count + severity dot otherwise.
  useEffect(() => {
    if (state.phase !== "ready") return;
    const { status, alerts } = state.data;
    const overall = status.overall.status;
    if (overall === "ok") {
      document.title = "BuildX Monitor";
    } else {
      const n = Math.max(alerts.length, status.overall.reasons.length, 1);
      const dot = overall === "error" ? "🔴" : "🟡";
      document.title = `${dot} ${n} issue${n === 1 ? "" : "s"} · BuildX Monitor`;
    }
  }, [state]);
  useEffect(
    () => () => {
      document.title = "BuildX Monitor — Health & CF Cost";
    },
    [],
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Best-effort kick of a live cycle; ignore rate-limit (429) — just reload data.
      await api.refresh().catch(() => undefined);
      await load();
      setRefreshTick((t) => t + 1);
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      <Header
        lastSynced={syncedAt}
        onRefresh={onRefresh}
        refreshing={refreshing}
        sampled={state.phase === "ready" && state.data.billing.sampled === true}
      />

      <main className="mx-auto max-w-6xl space-y-8 p-4 pb-16">
        {state.phase === "loading" && <LoadingSkeleton />}
        {state.phase === "error" && (
          <ErrorBanner message={state.message} onRetry={() => void load()} />
        )}
        {state.phase === "ready" && (
          <DashboardBody data={state.data} refreshTick={refreshTick} />
        )}
      </main>
    </div>
  );
}

// ── Body — order per mockup ──────────────────────────────────────────
function DashboardBody({ data, refreshTick }: { data: DashData; refreshTick: number }) {
  return (
    <>
      <HealthStrip status={data.status} alerts={data.alerts} />
      <BillingSummary billing={data.billing} />
      <QuotaCards billing={data.billing} />
      <ErrorsToday refreshTick={refreshTick} />
      <TrendsSection refreshTick={refreshTick} />
      <BillingHistorySection rows={data.history} capUsd={data.billing.budget.capUsd} />
      <PythaTable refreshTick={refreshTick} />
    </>
  );
}

// ── A. Health strip ──────────────────────────────────────────────────
function HealthStrip({ status, alerts }: { status: StatusResponse; alerts: AlertRow[] }) {
  const overall = status.overall.status;
  const [selected, setSelected] = useState<ServiceStatus | null>(null);

  // Alerts belonging to the currently-drilled service.
  const selectedAlerts = useMemo(
    () => (selected ? alerts.filter((a) => a.service === selected.service) : []),
    [selected, alerts],
  );

  const bannerTitle =
    overall === "ok"
      ? "Overall: Healthy"
      : overall === "warning"
        ? "Overall: Warning"
        : "Overall: Error";
  const bannerDetail =
    status.overall.reasons.length > 0
      ? status.overall.reasons.join(" · ")
      : overall === "ok"
        ? "All systems operational"
        : undefined;

  return (
    <section>
      <SectionTitle>Service health</SectionTitle>
      <Banner status={overall} title={bannerTitle} detail={bannerDetail} />

      {/* Compact active-alerts callout (deviation from mockup — keep the signal). */}
      {alerts.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="font-semibold">
            {alerts.length} active alert{alerts.length === 1 ? "" : "s"}
          </span>
          {": "}
          {alerts
            .slice(0, 3)
            .map((a) => a.message || `${a.service ?? "?"} ${a.kind ?? ""}`.trim())
            .join(" · ")}
          {alerts.length > 3 && ` · +${alerts.length - 3} more`}
        </div>
      )}

      {status.services.length === 0 ? (
        <Card className="mt-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No health data yet — waiting for the first poll.
          </p>
        </Card>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {status.services.map((s) => (
            <ServiceHealthCard key={s.service} service={s} onClick={() => setSelected(s)} />
          ))}
        </div>
      )}

      {selected && (
        <ServiceDrillModal
          service={selected}
          alerts={selectedAlerts}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

/** Color the uptime % by SLA band: >=99.9 green, >=99 amber, else red. */
function uptimeClass(pctVal: number): string {
  if (pctVal >= 99.9) return "text-green-600 dark:text-green-400";
  if (pctVal >= 99) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

function ServiceHealthCard({
  service,
  onClick,
}: {
  service: ServiceStatus;
  onClick: () => void;
}) {
  const st = normalizeHealth(service.status);

  const hasUptime = service.uptime24h !== null && Number.isFinite(service.uptime24h);
  const uptimeText = hasUptime ? `${(service.uptime24h as number).toFixed(2)}%` : "—";
  const uptimeColor = hasUptime
    ? uptimeClass(service.uptime24h as number)
    : "text-gray-400 dark:text-gray-500";

  // p50 prefers the real median; fall back to the latest probe responseMs.
  const p50 = service.p50Ms ?? service.responseMs;

  const errCount = service.errors ?? 0;
  const errClass =
    errCount > 0
      ? "text-red-600 dark:text-red-400"
      : "text-green-600 dark:text-green-400";

  const reason = service.reason || "all checks passing";

  return (
    <ServiceBorderCard status={st} onClick={onClick}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-mono font-semibold">{service.service}</span>
        <StatusPillDot status={st} />
      </div>

      {/* 3-metric inline row */}
      <div className="mt-3 flex gap-5">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Uptime 24h</div>
          <div className={"text-xl font-semibold tabular-nums " + uptimeColor}>
            {uptimeText}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">p50</div>
          <div className="text-xl font-semibold tabular-nums">{ms(p50)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Errors</div>
          <div className={"text-xl font-semibold tabular-nums " + errClass}>
            {big(errCount)}
          </div>
        </div>
      </div>

      {/* One-line reason */}
      <div className="mt-3 truncate border-t border-gray-100 pt-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
        {reason}
      </div>
    </ServiceBorderCard>
  );
}

// ── Service drill modal ──────────────────────────────────────────────
/** Severity → pill classes (error red / warning amber / else neutral). */
function severityClasses(sev: string | null): string {
  if (sev === "error" || sev === "critical")
    return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
  if (sev === "warning")
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300";
  return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
}

function ServiceDrillModal({
  service,
  alerts,
  onClose,
}: {
  service: ServiceStatus;
  alerts: AlertRow[];
  onClose: () => void;
}) {
  const st = normalizeHealth(service.status);

  const hasUptime = service.uptime24h !== null && Number.isFinite(service.uptime24h);
  const uptimeText = hasUptime ? `${(service.uptime24h as number).toFixed(2)}%` : "—";
  const uptimeColor = hasUptime
    ? uptimeClass(service.uptime24h as number)
    : "text-gray-400 dark:text-gray-500";

  const p50 = service.p50Ms ?? service.responseMs;
  const errCount = service.errors ?? 0;
  const errClass =
    errCount > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400";
  const reason = service.reason || "all checks passing";

  return (
    <Modal
      title={<span className="font-mono">{service.service}</span>}
      subtitle="Service health detail"
      onClose={onClose}
    >
      <div className="mb-4 flex items-center gap-2">
        <StatusPill status={st} />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          last checked {shortTime(service.ts)}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-gray-100 p-3 dark:bg-gray-700/40">
          <div className="text-xs text-gray-500 dark:text-gray-400">Uptime 24h</div>
          <div className={"mt-1 text-xl font-semibold tabular-nums " + uptimeColor}>
            {uptimeText}
          </div>
        </div>
        <div className="rounded-lg bg-gray-100 p-3 dark:bg-gray-700/40">
          <div className="text-xs text-gray-500 dark:text-gray-400">p50</div>
          <div className="mt-1 text-xl font-semibold tabular-nums">{ms(p50)}</div>
        </div>
        <div className="rounded-lg bg-gray-100 p-3 dark:bg-gray-700/40">
          <div className="text-xs text-gray-500 dark:text-gray-400">Errors</div>
          <div className={"mt-1 text-xl font-semibold tabular-nums " + errClass}>
            {big(errCount)}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
        {reason}
      </div>

      <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Active alerts ({alerts.length})
      </h3>
      {alerts.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No active alerts for this service.
        </p>
      ) : (
        <ul className="space-y-2">
          {alerts.map((a) => (
            <li
              key={a.id}
              className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={
                    "inline-flex rounded-full px-2 py-0.5 text-xs font-medium " +
                    severityClasses(a.severity)
                  }
                >
                  {a.severity ?? "info"}
                </span>
                {a.kind && (
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                    {a.kind}
                  </span>
                )}
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                  {shortTime(a.ts)}
                </span>
              </div>
              {a.message && (
                <div className="mt-1 text-sm text-gray-700 dark:text-gray-200">{a.message}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

// ── B. Cloudflare cost — this month ──────────────────────────────────
const CATEGORY_LABEL: Record<CostCategory, string> = {
  Workers: "Workers",
  R2: "R2",
  D1: "D1",
  KV: "KV",
  DO: "Durable Objects",
};

function BillingSummary({ billing }: { billing: BillingCurrent }) {
  const overLimit = useMemo(
    () => billing.resources.filter((r) => r.projectedPct >= 1).length,
    [billing.resources],
  );

  // Top cost driver: highest projected overage resource; fall back to top category.
  const topDriver = useMemo(() => {
    const res = [...billing.resources]
      .filter((r) => r.overageUsd > 0)
      .sort((a, b) => b.overageUsd - a.overageUsd)[0];
    if (res) {
      return { name: `${res.category} ${res.label}`, detail: `+${usd(res.overageUsd)} projected` };
    }
    const cats = Object.entries(billing.byCategory) as [CostCategory, number][];
    const top = cats.reduce(
      (best, cur) => (cur[1] > best[1] ? cur : best),
      ["Workers", 0] as [CostCategory, number],
    );
    if (top[1] > 0) {
      return { name: CATEGORY_LABEL[top[0]], detail: `${usd(top[1])} this month` };
    }
    return null;
  }, [billing.resources, billing.byCategory]);

  const baseUsd = billing.baseUsd;
  const overageUsd = Math.max(0, billing.totalUsd - baseUsd);

  // Prefer the worker's actuals-to-date figure; older workers omit it → run-rate.
  const hasBilledSoFar =
    typeof billing.billedSoFarUsd === "number" && Number.isFinite(billing.billedSoFarUsd);

  return (
    <section>
      <SectionTitle>Cloudflare cost — {monthLabel(billing.month)}</SectionTitle>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard
          label="Projected bill (month end)"
          value={usd(billing.totalUsd)}
          valueClass="text-red-600 dark:text-red-400"
          detail={`${usd(baseUsd)} base + ${usd(overageUsd)} overage`}
        />
        <SummaryCard
          label="Billed so far"
          value={usd(billing.billedSoFarUsd ?? billing.totalUsd)}
          valueClass="text-blue-600 dark:text-blue-400"
          detail={
            hasBilledSoFar
              ? "actuals to date"
              : "run-rate (= projected · actuals-to-date needs API)"
          }
        />
        <SummaryCard
          label="Metrics over limit"
          value={`${overLimit} / ${billing.resources.length}`}
          valueClass={
            overLimit > 0 ? "text-amber-600 dark:text-amber-400" : "text-gray-700 dark:text-gray-200"
          }
          detail="projected to exceed included"
        />
        <SummaryCard
          label="Top cost driver"
          value={topDriver ? topDriver.name : "—"}
          valueClass="text-red-600 dark:text-red-400"
          detail={topDriver ? topDriver.detail : "no overage — within free tier"}
        />
      </div>

      <Card className="mt-3">
        <BudgetGauge
          fraction={billing.budget.projectedPct}
          capUsd={billing.budget.capUsd}
          projectedUsd={billing.totalUsd}
        />
      </Card>
    </section>
  );
}

/** Flat secondary-surface summary card (no border). */
function SummaryCard({
  label,
  value,
  valueClass,
  detail,
}: {
  label: string;
  value: string;
  valueClass: string;
  detail: string;
}) {
  return (
    <div className="rounded-xl bg-gray-100 p-4 dark:bg-gray-800">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={"mt-1 truncate text-2xl font-semibold tabular-nums " + valueClass}>
        {value}
      </div>
      <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">{detail}</div>
    </div>
  );
}

// ── C. Quota usage ───────────────────────────────────────────────────
const QUOTA_GROUPS: CostCategory[] = ["Workers", "R2", "D1", "KV", "DO"];
const SERVICE_OPTIONS = ["All", "buildx-pytha", "buildx-onsite", "buildx-price"] as const;
type ServiceScope = (typeof SERVICE_OPTIONS)[number];

function QuotaCards({ billing }: { billing: BillingCurrent }) {
  const [scope, setScope] = useState<ServiceScope>("All");

  // Group resources by category (only categories that have usable resources).
  const grouped = useMemo(() => {
    const out: { category: CostCategory; resources: BillingResource[] }[] = [];
    for (const cat of QUOTA_GROUPS) {
      const res = billing.resources
        .filter((r) => r.category === cat && (r.projected > 0 || r.overageUsd > 0))
        .sort((a, b) => b.projectedPct - a.projectedPct);
      if (res.length > 0) out.push({ category: cat, resources: res });
    }
    return out;
  }, [billing.resources]);

  const hasAny = grouped.length > 0;

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Billable quota usage</SectionTitle>
        <div className="flex flex-wrap gap-1.5">
          {SERVICE_OPTIONS.map((opt) => {
            const active = scope === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => setScope(opt)}
                className={
                  "rounded-md border px-2.5 py-1 text-xs font-medium transition " +
                  (active
                    ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-300"
                    : "border-gray-300 bg-white text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700")
                }
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        Workers Paid · quotas pooled across workers. Bar colour by projected %: green &lt;80, amber
        80–99, red ≥100.
      </p>

      {scope !== "All" && (
        <p className="mb-3 text-[11px] text-gray-400 dark:text-gray-500">
          Showing pooled totals — per-service split needs API support.
        </p>
      )}

      {!hasAny ? (
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No metered usage yet this month.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => (
            <div key={g.category}>
              <h3 className="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
                {CATEGORY_LABEL[g.category]}
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {g.resources.map((r) => (
                  <QuotaCard key={r.resource} resource={r} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function QuotaCard({ resource: r }: { resource: BillingResource }) {
  const overLimit = r.projected > r.included;
  const projClass =
    r.projectedPct >= 1
      ? "text-red-600 dark:text-red-400"
      : r.projectedPct >= 0.8
        ? "text-amber-600 dark:text-amber-400"
        : "text-green-600 dark:text-green-400";

  // New worker fields (month-to-date actuals); fall back to projected-only view.
  const hasUsage = typeof r.usage === "number" && Number.isFinite(r.usage);
  const hasNow = typeof r.nowPct === "number" && Number.isFinite(r.nowPct);

  return (
    <Card>
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate font-semibold">{r.label}</span>
      </div>
      <div className="mt-1.5 flex items-baseline gap-1.5">
        <span className="text-lg font-semibold tabular-nums">
          {big(hasUsage ? r.usage : r.projected)}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          / {big(r.included)} incl
        </span>
      </div>
      <div className="mt-2">
        <ProgressBar fraction={r.projectedPct} />
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
        {hasNow ? (
          <span className="text-gray-400 dark:text-gray-500">now {pct(r.nowPct)}</span>
        ) : (
          <span title="now% needs current-to-date usage (API gap)">now —</span>
        )}
        <span className={"font-medium " + projClass}>proj {pct(r.projectedPct)}</span>
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-2 text-xs dark:border-gray-700">
        <span className="text-gray-500 dark:text-gray-400">proj. overage</span>
        <span
          className={
            "font-semibold " +
            (overLimit ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400")
          }
        >
          {overLimit ? `+${usd(r.overageUsd)}` : usd(0)}
        </span>
      </div>
    </Card>
  );
}

// ── Billing history (chart + table) ──────────────────────────────────
function BillingHistorySection({
  rows,
  capUsd,
}: {
  rows: BillingMonthRow[];
  capUsd: number;
}) {
  return (
    <section>
      <SectionTitle>Monthly billing history</SectionTitle>
      {rows.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No billing history yet — the first month will appear after metrics ingest.
          </p>
        </Card>
      ) : (
        <Card>
          <BillingHistoryChart rows={rows} capUsd={capUsd} />
          <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
            Lighter/outlined bar = current month projected (run-rate estimate). Sampled metrics are
            estimates.
          </p>
        </Card>
      )}
    </section>
  );
}

// ── States ───────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true">
      <div className="h-16 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-800" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-28 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-800"
          />
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-xl bg-gray-200 dark:bg-gray-800" />
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card className="border-red-300 dark:border-red-800">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-medium text-red-700 dark:text-red-400">
            Couldn’t load dashboard
          </div>
          <div className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{message}</div>
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    </Card>
  );
}
